import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import { Box, Button, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Dispatch, SetStateAction, memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { bloxColors, bloxValues } from 'themes/constants';
import { compactIconFontSize, getCompactButtonSx } from '../Shared/NewActionButton';

type AISearchToggleButtonProps = {
  show: boolean;
  aiSearchActive: boolean;
  setAiSearchActive: Dispatch<SetStateAction<boolean>>;
  onAiSearchActivate: () => void;
  onAiSearchDeactivate: () => void;
  onToggleComplete?: () => void;
};

export const AISearchToggleButton = memo(
  ({
    show,
    aiSearchActive,
    setAiSearchActive,
    onAiSearchActivate,
    onAiSearchDeactivate,
    onToggleComplete,
  }: AISearchToggleButtonProps) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const handleOnClick = () => {
      const next = !aiSearchActive;
      if (next) {
        onAiSearchActivate();
      } else {
        onAiSearchDeactivate();
      }
      setAiSearchActive(next);
      onToggleComplete?.();
    };

    const buttonSx = useMemo(() => {
      const compact = getCompactButtonSx(isMobile);
      return {
        flexShrink: 0,
        ...compact,
        textTransform: 'none',
      };
    }, [isMobile]);

    const boxSx = useMemo(() => {
      return {
        display: 'inline-flex',
        alignItems: 'center',
        px: 0.5,
        py: 0.125,
        borderRadius: 999,
        fontSize: bloxValues.microFontSizeRem,
        lineHeight: 1,
        textTransform: 'uppercase',
        bgcolor:
          theme.palette.mode === 'light'
            ? alpha(bloxColors.white, 0.24)
            : alpha(bloxColors.white, 0.16),
        border: `1px solid ${alpha(bloxColors.white, 0.4)}`,
      };
    }, [theme.palette.mode]);

    if (!show) {
      return null;
    }

    return (
      <Tooltip
        title={
          aiSearchActive
            ? t('blox.ai.search.toggle_off_tooltip', 'Return to standard search')
            : t('blox.ai.search.toggle_on_tooltip', 'AI Search (Beta)')
        }
      >
        <Button
          size="small"
          variant={aiSearchActive ? 'contained' : 'outlined'}
          disableElevation
          startIcon={isMobile ? undefined : <AutoAwesomeOutlinedIcon fontSize="small" />}
          onClick={handleOnClick}
          data-testid="ai-search-toggle-btn"
          sx={buttonSx}
        >
          {isMobile ? (
            <AutoAwesomeOutlinedIcon fontSize={compactIconFontSize} />
          ) : (
            <Box
              component="span"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.75,
              }}
            >
              {t('blox.ai.search.toggle_label', 'AI Search')}
              {aiSearchActive ? (
                <Box
                  component="span"
                  sx={boxSx}
                >
                  {t('blox.general.on')}
                </Box>
              ) : null}
            </Box>
          )}
        </Button>
      </Tooltip>
    );
  },
);
AISearchToggleButton.displayName = 'AISearchToggleButton';
